/**
 * COMP-36: Bracket Interaction Hook
 * Manages interactive bracket functionality with drag-and-drop
 */

import { useState, useCallback } from 'react';
import { Tournament, Match, Participant } from '../types/tournament';

interface UseBracketInteractionProps {
  tournament: Tournament;
  matches: Match[];
  participants: Participant[];
  onMatchUpdate?: (matchId: string, updates: Partial<Match>) => void;
  onParticipantMove?: (participantId: number, newPosition: number) => void;
}

interface UseBracketInteractionReturn {
  handleMatchClick: (matchId: string) => void;
  handleParticipantDrag: (participantId: number, event: React.DragEvent) => void;
  handleParticipantDrop: (targetPosition: number, event: React.DragEvent) => void;
  isValidDrop: (participantId: number, targetPosition: number) => boolean;
  selectedMatch: string | null;
  draggedParticipant: number | null;
  hoveredPosition: number | null;
}

export const useBracketInteraction = ({
  tournament: _tournament,
  matches: _matches,
  participants,
  onMatchUpdate: _onMatchUpdate,
  onParticipantMove
}: UseBracketInteractionProps): UseBracketInteractionReturn => {
  const [selectedMatch, setSelectedMatch] = useState<string | null>(null);
  const [draggedParticipant, setDraggedParticipant] = useState<number | null>(null);
  const [hoveredPosition, setHoveredPosition] = useState<number | null>(null);

  const handleMatchClick = useCallback((matchId: string) => {
    setSelectedMatch(prev => prev === matchId ? null : matchId);
  }, []);

  const handleParticipantDrag = useCallback((participantId: number, event: React.DragEvent) => {
    setDraggedParticipant(participantId);
    event.dataTransfer.setData('text/plain', participantId.toString());
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleParticipantDrop = useCallback((targetPosition: number, event: React.DragEvent) => {
    event.preventDefault();

    const participantId = parseInt(event.dataTransfer.getData('text/plain'));

    if (isValidDrop(participantId, targetPosition)) {
      onParticipantMove?.(participantId, targetPosition);
    }

    setDraggedParticipant(null);
    setHoveredPosition(null);
  }, [onParticipantMove]);

  const isValidDrop = useCallback((participantId: number, targetPosition: number): boolean => {
    // Basic validation rules for bracket movement
    const participant = participants.find(p => p.id === participantId);
    if (!participant) return false;

    // Don't allow dropping on the same position
    if (participant.seed === targetPosition) return false;

    // Check if target position is available
    // Check if target position is occupied
    participants.some(p => p.seed === targetPosition);

    // For now, allow swapping positions
    return true;
  }, [participants]);

  return {
    handleMatchClick,
    handleParticipantDrag,
    handleParticipantDrop,
    isValidDrop,
    selectedMatch,
    draggedParticipant,
    hoveredPosition
  };
};

export default useBracketInteraction;